// pages/Settings.jsx — Tabbed enterprise settings module
import React, { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { usePermission } from '../lib/permissions';
import api from '../lib/api';
import { Avatar } from './AppLayout';
import { Building2, Users2, Flag, Tag, Mail, BellRing, Shield, Plus, X, Save, Briefcase, Edit2 } from 'lucide-react';
import Departments from './Departments';
import Designations from './Designations';

const TAB_DEFS = [
  { k: 'company', l: 'Company', Icon: Building2, perm: 'settings.edit_org' },
  { k: 'departments', l: 'Departments', Icon: Users2, perm: 'departments.view' },
  { k: 'designations', l: 'Designations', Icon: Briefcase, perm: 'designations.view' },
  { k: 'priorities', l: 'Priorities', Icon: Flag, perm: 'settings.edit_org' },
  { k: 'labels', l: 'Task Labels', Icon: Tag, perm: 'settings.edit_labels' },
  { k: 'templates', l: 'Email Templates', Icon: Mail, perm: 'settings.edit_templates' },
  { k: 'notifications', l: 'Notifications', Icon: BellRing, perm: 'settings.edit_notifications' },
  { k: 'security', l: 'Security', Icon: Shield, perm: 'settings.edit_security' },
];

export default function Settings() {
  const { user } = useAuth();
  const { has } = usePermission();
  const canEditOrg = has('settings.edit_org');
  const tabs = TAB_DEFS.filter((t) => has(t.perm) || t.k === 'notifications');
  const [tab, setTab] = useState(tabs[0]?.k || 'notifications');

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="settings-page">
      <div style={{ color: 'var(--trello-text)', marginBottom: 20 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>Settings</h1>
        <p style={{ opacity: 0.85, marginTop: 4, color: 'var(--trello-text-muted)' }}>{canEditOrg ? 'Workspace configuration' : 'Your preferences'}</p>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap" data-testid="settings-tabs">
        {tabs.map(({ k, l, Icon }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={tab === k ? "tm-btn" : "tm-btn-ghost"}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 20, padding: '8px 16px', fontSize: 13, fontWeight: 600 }}>
            <Icon size={14} /> {l}
          </button>
        ))}
      </div>

      <div className="tm-stat-card" style={{ borderLeft: 'none' }}>
        {tab === 'company' && <CompanyProfile user={user} />}
        {tab === 'departments' && <Departments />}
        {tab === 'designations' && <Designations />}
        {tab === 'priorities' && <Priorities />}
        {tab === 'labels' && <Labels />}
        {tab === 'templates' && <EmailTemplates />}
        {tab === 'notifications' && <NotificationPrefs />}
        {tab === 'security' && <Security />}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 12, alignItems: 'center', padding: '10px 0', borderTop: '1px solid var(--trello-border)' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--trello-text)' }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

// ============ Company profile ============
function CompanyProfile({ user }) {
  const { has } = usePermission();
  const [company, setCompany] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => { api.get('/settings/company').then(({ data }) => setCompany(data.company)); }, []);
  if (!company) return <div>Loading…</div>;
  const save = async () => {
    setBusy(true); setMsg('');
    try { await api.patch('/settings/company', company); setMsg('Saved'); }
    catch (e) { setMsg(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); setTimeout(() => setMsg(''), 2500); }
  };
  const disabled = !has('settings.edit_org');
  return (
    <div data-testid="company-profile">
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 6px' }}>Company profile</h3>
      <p style={{ color: 'var(--trello-text-muted)', fontSize: 13, margin: '0 0 12px' }}>Visible to your team across every workspace surface.</p>
      <Row label="Company name">
        <input className="tm-input" value={company.name || ''} onChange={(e) => setCompany({ ...company, name: e.target.value })} disabled={disabled} data-testid="company-name" />
      </Row>
      <Row label="Logo URL">
        <input className="tm-input" placeholder="https://…" value={company.logo_url || ''} onChange={(e) => setCompany({ ...company, logo_url: e.target.value })} disabled={disabled} data-testid="company-logo" />
      </Row>
      <Row label="Website">
        <input className="tm-input" value={company.website || ''} onChange={(e) => setCompany({ ...company, website: e.target.value })} disabled={disabled} data-testid="company-website" />
      </Row>
      <Row label="Phone">
        <input className="tm-input" value={company.phone || ''} onChange={(e) => setCompany({ ...company, phone: e.target.value })} disabled={disabled} data-testid="company-phone" />
      </Row>
      <Row label="Timezone">
        <input className="tm-input" value={company.timezone || 'UTC'} onChange={(e) => setCompany({ ...company, timezone: e.target.value })} disabled={disabled} data-testid="company-timezone" />
      </Row>
      <Row label="Address">
        <textarea className="tm-input" rows={3} value={company.address || ''} onChange={(e) => setCompany({ ...company, address: e.target.value })} disabled={disabled} data-testid="company-address" />
      </Row>
      {!disabled && (
        <div className="mt-3 flex items-center gap-2">
          <button className="tm-btn" onClick={save} disabled={busy} data-testid="company-save"><Save size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: -2 }} />{busy ? 'Saving…' : 'Save changes'}</button>
          {msg && <span style={{ fontSize: 13, color: msg === 'Saved' ? '#61BD4F' : '#DE350B' }}>{msg}</span>}
        </div>
      )}
    </div>
  );
}


// ============ Priorities ============
function Priorities() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ slug: '', label: '', color: '#0079BF' });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = async () => { const { data } = await api.get('/settings/priorities'); setItems(data.priorities); };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.slug || !form.label) return;
    try { await api.post('/settings/priorities', form); setForm({ slug: '', label: '', color: '#0079BF' }); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const del = async (id) => { if (!confirm('Delete priority?')) return; await api.delete(`/settings/priorities/${id}`); load(); };

  const startEdit = (p) => { setEditing(p.id); setEditForm({ slug: p.slug, label: p.label, color: p.color }); };
  const cancelEdit = () => { setEditing(null); setEditForm({}); };
  const saveEdit = async (id) => {
    if (!editForm.slug || !editForm.label) return;
    try { await api.patch(`/settings/priorities/${id}`, editForm); setEditing(null); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  return (
    <div data-testid="priorities-tab">
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 6px' }}>Custom priorities</h3>
      <p style={{ color: 'var(--trello-text-muted)', fontSize: 13, margin: '0 0 12px' }}>Extend the built-in Critical/High/Medium/Low with your own labels.</p>
      <div className="flex gap-2 mb-4 flex-wrap">
        <input className="tm-input" placeholder="slug (e.g. blocker)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })} style={{ maxWidth: 140 }} data-testid="prio-slug" />
        <input className="tm-input" placeholder="Label (e.g. Blocker)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} style={{ maxWidth: 200 }} data-testid="prio-label" />
        <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} style={{ width: 44, height: 36, border: 'none', cursor: 'pointer', borderRadius: 4 }} data-testid="prio-color" />
        <button className="tm-btn" onClick={add} data-testid="prio-add"><Plus size={14} /> Add</button>
      </div>
      <div className="space-y-2">
        {items.map((p) => (
          <div key={p.id} data-testid={`prio-${p.slug}`} className="flex items-center gap-3 p-2 rounded" style={{ background: 'var(--trello-list-bg)' }}>
            {editing === p.id ? (
              <>
                <input className="tm-input" value={editForm.label} onChange={(e) => setEditForm({ ...editForm, label: e.target.value })} style={{ width: 120, padding: '4px 8px' }} />
                <input className="tm-input" value={editForm.slug} onChange={(e) => setEditForm({ ...editForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })} style={{ width: 100, fontFamily: 'monospace', padding: '4px 8px' }} />
                <input type="color" value={editForm.color} onChange={(e) => setEditForm({ ...editForm, color: e.target.value })} style={{ width: 36, height: 32, border: 'none', cursor: 'pointer', borderRadius: 4 }} />
                <div style={{ flex: 1 }} />
                <button className="tm-btn" onClick={() => saveEdit(p.id)} style={{ padding: '4px 10px' }}>Save</button>
                <button className="tm-btn-ghost" onClick={cancelEdit}><X size={14} /></button>
              </>
            ) : (
              <>
                <span className="tm-label-pill" style={{ background: p.color, minWidth: 90, cursor: 'pointer' }} onClick={() => startEdit(p)} title="Click to edit">{p.label}</span>
                <span style={{ flex: 1, fontFamily: 'monospace', fontSize: 12, color: 'var(--trello-text-muted)', cursor: 'pointer' }} onClick={() => startEdit(p)} title="Click to edit">{p.slug}</span>
                {p.is_default ? <span style={{ fontSize: 11, color: 'var(--trello-text-muted)' }}>default</span>
                  : <button className="tm-btn-ghost" onClick={() => del(p.id)} data-testid={`prio-del-${p.slug}`} title="Delete"><X size={14} /></button>}
                <button className="tm-btn-ghost" onClick={() => startEdit(p)} title="Edit"><Edit2 size={14} /></button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ Labels ============
function Labels() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: '', color: '#61BD4F' });
  const load = async () => { const { data } = await api.get('/settings/labels'); setItems(data.labels); };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.name.trim()) return;
    await api.post('/settings/labels', form); setForm({ name: '', color: '#61BD4F' }); load();
  };
  const del = async (id) => { if (!confirm('Delete label?')) return; await api.delete(`/settings/labels/${id}`); load(); };
  return (
    <div data-testid="labels-tab">
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 6px' }}>Custom task labels</h3>
      <p style={{ color: 'var(--trello-text-muted)', fontSize: 13, margin: '0 0 12px' }}>Reusable tags that surface as colored strips on task cards.</p>
      <div className="flex gap-2 mb-4">
        <input className="tm-input" placeholder="Label name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="label-name" />
        <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} style={{ width: 44, height: 36, border: 'none', cursor: 'pointer', borderRadius: 4 }} data-testid="label-color" />
        <button className="tm-btn" onClick={add} data-testid="label-add"><Plus size={14} /> Add</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {items.map((l) => (
          <div key={l.id} data-testid={`label-${l.id}`} className="flex items-center gap-2 p-2 rounded" style={{ background: 'var(--trello-list-bg)' }}>
            <span className="tm-label-pill" style={{ background: l.color, minWidth: 100 }}>{l.name}</span>
            <button className="tm-btn-ghost" onClick={() => del(l.id)} data-testid={`label-del-${l.id}`}><X size={14} /></button>
          </div>
        ))}
        {items.length === 0 && <div style={{ color: 'var(--trello-text-muted)', fontSize: 13, gridColumn: '1/-1' }}>No custom labels yet.</div>}
      </div>
    </div>
  );
}

// ============ Email templates ============
function EmailTemplates() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const load = async () => { const { data } = await api.get('/settings/email-templates'); setItems(data.templates); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!sel) return;
    await api.put(`/settings/email-templates/${sel.key_name}`, { subject: sel.subject, body_html: sel.body_html });
    alert('Saved.'); load();
  };
  return (
    <div data-testid="templates-tab">
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 6px' }}>Email templates</h3>
      <p style={{ color: 'var(--trello-text-muted)', fontSize: 13, margin: '0 0 12px' }}>Override the wording of transactional emails (password reset, invitations, mentions, etc.). Placeholders: {'{{full_name}}, {{task_title}}, {{project_name}}, {{reset_link}}, {{accept_link}}, {{task_link}}, {{actor_name}}, {{comment_body}}, {{due_date}}, {{company_name}}'}</p>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(0,220px) minmax(0,1fr)' }}>
        <div className="space-y-1">
          {items.map((t) => (
            <button key={t.key_name} onClick={() => setSel({ ...t })} data-testid={`tpl-${t.key_name}`}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 8, border: 'none', background: sel?.key_name === t.key_name ? 'var(--trello-hover)' : 'transparent', cursor: 'pointer', fontSize: 13 }}>
              <div style={{ fontWeight: 600, color: 'var(--trello-text)' }}>{t.key_name.replace(/_/g, ' ')}</div>
              <div style={{ fontSize: 11, color: 'var(--trello-text-muted)' }}>{t.is_default ? 'default' : 'customized'}</div>
            </button>
          ))}
        </div>
        <div>
          {!sel && <div style={{ fontSize: 13, color: 'var(--trello-text-muted)' }}>Select a template to edit.</div>}
          {sel && (
            <div className="space-y-3">
              <div>
                <label className="tm-label">Subject</label>
                <input className="tm-input" value={sel.subject} onChange={(e) => setSel({ ...sel, subject: e.target.value })} data-testid="tpl-subject" />
              </div>
              <div>
                <label className="tm-label">HTML body</label>
                <textarea className="tm-input" rows={10} value={sel.body_html} onChange={(e) => setSel({ ...sel, body_html: e.target.value })} data-testid="tpl-body" style={{ fontFamily: 'monospace', fontSize: 12 }} />
              </div>
              <button className="tm-btn" onClick={save} data-testid="tpl-save"><Save size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: -2 }} />Save template</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Notification prefs ============
function NotificationPrefs() {
  const [prefs, setPrefs] = useState(null);
  useEffect(() => { api.get('/settings/notifications').then(({ data }) => setPrefs(data.prefs)); }, []);
  if (!prefs) return <div>Loading…</div>;
  const toggle = async (k) => {
    const next = { ...prefs, [k]: !prefs[k] };
    setPrefs(next);
    await api.put('/settings/notifications', { [k]: next[k] });
  };
  const LABELS = {
    task_assigned: 'When a task is assigned to me',
    comment_added: 'When someone comments on my task',
    comment_mention: 'When I am @mentioned',
    due_soon: 'When my task is due soon',
    project_shared: 'When a project is shared with me',
    delete_requested: 'When a delete is requested (Leader only)',
    delete_decision: 'When my delete request is approved/rejected',
    digest_email: 'Weekly digest email',
  };
  return (
    <div data-testid="notifications-tab">
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 6px' }}>Notification preferences</h3>
      <p style={{ color: 'var(--trello-text-muted)', fontSize: 13, margin: '0 0 12px' }}>Choose what triggers in-app notifications for you.</p>
      <div>
        {Object.entries(LABELS).map(([k, l]) => (
          <label key={k} className="flex items-center gap-3 py-2" style={{ borderTop: '1px solid var(--trello-border)' }} data-testid={`pref-${k}`}>
            <input type="checkbox" checked={!!prefs[k]} onChange={() => toggle(k)} />
            <span style={{ fontSize: 14 }}>{l}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ============ Security ============
function Security() {
  const [sec, setSec] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/settings/security').then(({ data }) => setSec(data.security)); }, []);
  if (!sec) return <div>Loading…</div>;
  const save = async () => {
    setBusy(true);
    try { await api.put('/settings/security', sec); alert('Saved.'); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="security-tab">
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 6px' }}>Security</h3>
      <p style={{ color: 'var(--trello-text-muted)', fontSize: 13, margin: '0 0 12px' }}>Password policy and workspace security controls.</p>
      <Row label="Minimum password length">
        <input type="number" min="6" max="64" className="tm-input" value={sec.min_password_length}
          onChange={(e) => setSec({ ...sec, min_password_length: Number(e.target.value) })} data-testid="sec-minlen" style={{ maxWidth: 120 }} />
      </Row>
      <div style={{ display: 'none' }}>
        <Row label="Require 2FA">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!sec.require_2fa} onChange={(e) => setSec({ ...sec, require_2fa: e.target.checked ? 1 : 0 })} data-testid="sec-2fa" />
            <span style={{ fontSize: 12, color: 'var(--trello-text-muted)' }}>Enforce for all users (roadmap — enforcement lands in v8)</span>
          </label>
        </Row>

        <Row
          label="Session timeout (hours1)">
          <input type="number" min="48" max="720" className="tm-input" value={sec.session_timeout_hours}
            onChange={(e) => setSec({ ...sec, session_timeout_hours: Number(e.target.value) })} data-testid="sec-timeout" style={{ maxWidth: 120 }} />
        </Row>

        <Row label="Allow Google OAuth">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!sec.allow_google_oauth} onChange={(e) => setSec({ ...sec, allow_google_oauth: e.target.checked ? 1 : 0 })} data-testid="sec-oauth" />
            <span style={{ fontSize: 12, color: 'var(--trello-text-muted)' }}>When off, hide the Google Sign-in button on /login.</span>
          </label>
        </Row>
      </div>
      <div className="mt-3">
        <button className="tm-btn" onClick={save} disabled={busy} data-testid="sec-save"><Save size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: -2 }} />{busy ? 'Saving…' : 'Save security settings'}</button>
      </div>
    </div>
  );
}
